import React, { useState, useEffect } from "react";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Avatar,
  Box,
  Tooltip,
  Divider,
  Typography,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  ExpandLess,
  ExpandMore,
  Home,
  Business,
  Store,
  People,
  Security,
  BarChart,
  PeopleOutline,
  PeopleTwoTone,
  Diversity3,
  PinDrop,
  Source,
  AccountBalance,
  LocalShipping,
  AdminPanelSettings,
  Article,
  LocalPharmacy,
  CalendarMonth,
  MedicalServices,
} from "@mui/icons-material";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import ArticleIcon from '@mui/icons-material/Article';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';

//---------------- Auth---------------- 
import { AuthProvider, useAuth } from "./AuthContext";

const Sidebar = ({ open }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery('(max-width:768px)');
  const history = useHistory();
  const location = useLocation();
  
  const [masterOpen, setMasterOpen] = useState(false);
  const [tranascationOpen, setTransactionOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [pharmacyOpen, setPharmacyOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  
  // Mobile menu states
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [activeMenu, setActiveMenu] = useState(null);
  const [bottomNavValue, setBottomNavValue] = useState(0);

  const [subMenuOpen, setSubMenuOpen] = useState({
    purchase: false,
    sale: false,
    stocks: false,
    Accounts: false,
  });

  const toggleSubMenu = (menu) => {
    setSubMenuOpen((prev) => ({
      ...prev,
      [menu]: !prev[menu],
    }));
  };

  const { companyName, companyId, isSuperAdmin, superCompanyId, permissions, roleName, isAppointmentNeeded } = useAuth();

  // DEBUG: Check isAppointmentNeeded value
  console.log('🔍 Sidebar - isAppointmentNeeded:', isAppointmentNeeded, 'Type:', typeof isAppointmentNeeded);

  {/* Permissions */ }
  const hasReadMasterPermission = permissions.includes('Read Master');
  const hasReadTransactionPermission = permissions.includes('Read Transaction');
  const hasReadAccountsPermission = permissions.includes('Read Accounts');
  const hasReadAdministrationPermission = permissions.includes('Read Administration');
  const hasReadReadReportsPermission = permissions.includes('Read Reports');

  // Update bottom nav value based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path.includes('Appointment') || path.includes('DoctorEntry') || path.includes('Weekly') || path.includes('Diary')) {
      setBottomNavValue(1);
    } else if (path.includes('Patient') || path.includes('Doctor') || path.includes('Treatment') || path.includes('Company') || path.includes('Department') || path.includes('Item')) {
      setBottomNavValue(0);
    } else if (path.includes('Income') || path.includes('Expense')) {
      setBottomNavValue(2);
    } else if (path.includes('Pharmacy')) {
      setBottomNavValue(3);
    } else if (path.includes('User') || path.includes('Role') || path.includes('Password')) {
      setBottomNavValue(4);
    }
  }, [location.pathname]);

  // Mobile menu handlers
  const handleMobileMenuOpen = (event, menuType) => {
    setMobileMenuAnchor(event.currentTarget);
    setActiveMenu(menuType);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
    setActiveMenu(null);
  };

  const handleMobileNavigation = (path) => {
    history.push(path);
    handleMobileMenuClose();
  };

  // Mobile Bottom Navigation Component
  if (isMobile) {
    return (
      <>
        <Paper 
          sx={{ 
            position: 'fixed', 
            bottom: 0, 
            left: 0, 
            right: 0, 
            zIndex: 1200,
            borderTop: '1px solid rgba(0,0,0,0.1)',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
          }} 
          elevation={3}
        >
          <BottomNavigation
            value={bottomNavValue}
            onChange={(event, newValue) => setBottomNavValue(newValue)}
            sx={{
              background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1419 100%)',
              height: '65px',
              '& .MuiBottomNavigationAction-root': {
                color: 'rgba(255,255,255,0.6)',
                minWidth: '60px',
                padding: '6px 0',
                '&.Mui-selected': {
                  color: '#667eea',
                },
              },
              '& .MuiBottomNavigationAction-label': {
                fontSize: '10px',
                fontWeight: 600,
                marginTop: '2px',
              },
            }}
          >
            {hasReadMasterPermission && (
              <BottomNavigationAction 
                label="Master" 
                icon={<Business sx={{ fontSize: '22px' }} />}
                onClick={(e) => handleMobileMenuOpen(e, 'master')}
              />
            )}
            {hasReadMasterPermission && (
              <BottomNavigationAction 
                label="Services" 
                icon={<CalendarMonth sx={{ fontSize: '22px' }} />}
                onClick={(e) => handleMobileMenuOpen(e, 'services')}
              />
            )}
            {hasReadAccountsPermission && (
              <BottomNavigationAction 
                label="Accounts" 
                icon={<AccountBalanceWalletIcon sx={{ fontSize: '22px' }} />}
                onClick={(e) => handleMobileMenuOpen(e, 'accounts')}
              />
            )}
            {hasReadMasterPermission && (
              <BottomNavigationAction 
                label="Pharmacy" 
                icon={<LocalPharmacy sx={{ fontSize: '22px' }} />}
                onClick={() => history.push('/PharmacyIndex')}
              />
            )}
            {hasReadAdministrationPermission && (
              <BottomNavigationAction 
                label="Auth" 
                icon={<People sx={{ fontSize: '22px' }} />}
                onClick={(e) => handleMobileMenuOpen(e, 'auth')}
              />
            )}
          </BottomNavigation>
        </Paper>

        {/* Master Menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={activeMenu === 'master'}
          onClose={handleMobileMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          PaperProps={{
            sx: {
              background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1419 100%)',
              color: 'white',
              borderRadius: '12px',
              minWidth: '180px',
              mb: 1,
            }
          }}
        >
          <MenuItem onClick={() => handleMobileNavigation('/CompanyEdit')} sx={{ fontSize: '13px', py: 1.5 }}>🏢 Company</MenuItem>
          <MenuItem onClick={() => handleMobileNavigation('/DepartmentIndex')} sx={{ fontSize: '13px', py: 1.5 }}>📋 Department & QA</MenuItem>
          <MenuItem onClick={() => handleMobileNavigation('/ItemDashboard')} sx={{ fontSize: '13px', py: 1.5 }}>📦 Item</MenuItem>
          <MenuItem onClick={() => handleMobileNavigation('/PatientIndex')} sx={{ fontSize: '13px', py: 1.5 }}>👤 Patient</MenuItem>
          <MenuItem onClick={() => handleMobileNavigation('/DoctorIndex')} sx={{ fontSize: '13px', py: 1.5 }}>👨‍⚕️ Doctor</MenuItem>
          <MenuItem onClick={() => handleMobileNavigation('/TreatmentsIndex')} sx={{ fontSize: '13px', py: 1.5 }}>💊 Treatment</MenuItem>
        </Menu>

        {/* Services Menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={activeMenu === 'services'}
          onClose={handleMobileMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          PaperProps={{
            sx: {
              background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1419 100%)',
              color: 'white',
              borderRadius: '12px',
              minWidth: '180px',
              mb: 1,
            }
          }}
        >
          <MenuItem onClick={() => handleMobileNavigation('/AppointmentIndex')} sx={{ fontSize: '13px', py: 1.5 }}>📅 Appointment</MenuItem>
          <MenuItem onClick={() => handleMobileNavigation('/DoctorEntryIndex')} sx={{ fontSize: '13px', py: 1.5 }}>🏥 Doctor Entry</MenuItem>
          {isAppointmentNeeded && (
            <MenuItem onClick={() => handleMobileNavigation('/WeeklySchedule')} sx={{ fontSize: '13px', py: 1.5 }}>📆 Weekly Template</MenuItem>
          )}
          {isAppointmentNeeded && (
            <MenuItem onClick={() => handleMobileNavigation('/DoctorMontlyDiary')} sx={{ fontSize: '13px', py: 1.5 }}>📖 Doctor Diary</MenuItem>
          )}
        </Menu>

        {/* Accounts Menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={activeMenu === 'accounts'}
          onClose={handleMobileMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          PaperProps={{
            sx: {
              background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1419 100%)',
              color: 'white',
              borderRadius: '12px',
              minWidth: '180px',
              mb: 1,
            }
          }}
        >
          <MenuItem onClick={() => handleMobileNavigation('/IncomeExpenseIndex')} sx={{ fontSize: '13px', py: 1.5 }}>💰 Income & Expense</MenuItem>
        </Menu>

        {/* Auth Menu */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={activeMenu === 'auth'}
          onClose={handleMobileMenuClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          PaperProps={{
            sx: {
              background: 'linear-gradient(180deg, #1a1f3a 0%, #0f1419 100%)',
              color: 'white',
              borderRadius: '12px',
              minWidth: '180px',
              mb: 1,
            }
          }}
        >
          <MenuItem onClick={() => handleMobileNavigation('/UserIndex')} sx={{ fontSize: '13px', py: 1.5 }}>👥 Users</MenuItem>
          <MenuItem onClick={() => handleMobileNavigation('/RoleIndex')} sx={{ fontSize: '13px', py: 1.5 }}>🔐 Roles</MenuItem>
          <MenuItem onClick={() => handleMobileNavigation('/PasswordChange')} sx={{ fontSize: '13px', py: 1.5 }}>🔑 Reset Password</MenuItem>
        </Menu>
      </>
    );
  }

  // Desktop Sidebar (original code)

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? 260 : 70,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: open ? 260 : 70,
          background: "linear-gradient(180deg, #1a1f3a 0%, #0f1419 100%)",
          color: "white",
          overflowX: "hidden",
          overflowY: "auto",
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          borderRight: "1px solid rgba(255, 255, 255, 0.08)",
          boxShadow: "4px 0 24px rgba(0, 0, 0, 0.3)",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            background: "rgba(255, 255, 255, 0.05)",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "rgba(255, 255, 255, 0.2)",
            borderRadius: "3px",
            "&:hover": {
              background: "rgba(255, 255, 255, 0.3)",
            },
          },
        },
      }}
    >
      {/* Modern Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: open ? 3 : 2,
          justifyContent: open ? "flex-start" : "center",
          // background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          minHeight: "20px",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            //background: "radial-gradient(circle at top right, rgba(255,255,255,0.1), transparent)",
          },
        }}
      >
        {open && (
          <Box sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "18px",
                letterSpacing: "0.5px",
                textShadow: "0 2px 4px rgba(0,0,0,0.2)",
              }}
            >

            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "11px",
                opacity: 0.9,
                display: "block",
                marginTop: "2px",
              }}
            >

            </Typography>
          </Box>
        )}
        {!open && (
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: 700,
            }}
          >
            M
          </Typography>
        )}
      </Box>
      <Divider sx={{ bgcolor: "rgba(255,255,255,0.1)", height: "1px" }} />

      <List sx={{ marginTop: '12px', px: 1 }}>

        {/* Master Section */}
        {hasReadMasterPermission && (
          <ListItemButton
            onClick={() => setMasterOpen(!masterOpen)}
            sx={{
              borderRadius: "12px",
              mb: 0.5,
              transition: "all 0.3s ease",
              "&:hover": {
                background: "rgba(102, 126, 234, 0.15)",
                transform: "translateX(4px)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: open ? 40 : "auto" }}>
              <Business sx={{
                color: "white",
                fontSize: "22px",
              }} />
            </ListItemIcon>
            {open &&
              <ListItemText
                primary="Master"
                primaryTypographyProps={{
                  style: {
                    cursor: 'pointer',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                  },
                }}
              />}

            {open && (masterOpen ?
              <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} /> :
              <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
            )}
          </ListItemButton>
        )}

        <Collapse in={masterOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            background: "rgba(102, 126, 234, 0.08)",
            borderRadius: "12px",
            mb: 1,
            overflow: "hidden",
          }}>
            <List component="div" disablePadding sx={{ py: 0.5 }}>

              <ListItemButton
                component={NavLink}
                to="/CompanyEdit"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Company"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>
              <ListItemButton
                component={NavLink}
                to="/DepartmentIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Department & QA"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/ItemDashboard"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #764ba2",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Item"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/PatientIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Patient"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

              

              <ListItemButton
                component={NavLink}
                to="/DoctorIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Doctor"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/TreatmentsIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(102, 126, 234, 0.3)",
                    borderLeft: "3px solid #667eea",
                    color: "white",
                  },
                }}
              >
                {open && (
                  <ListItemText
                    primary="Treatment"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: "20px",
                        fontFamily: "system-ui, -apple-system, sans-serif",
                        fontSize: "13px",
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }}
                  />
                )}
              </ListItemButton>

            </List>
          </Box>
        </Collapse >


        {hasReadMasterPermission && (
          <ListItemButton
            onClick={() => setTransactionOpen(!tranascationOpen)}
            sx={{
              borderRadius: "12px",
              mb: 0.5,
              transition: "all 0.3s ease",
              "&:hover": {
                background: "rgba(102, 126, 234, 0.15)",
                transform: "translateX(4px)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: open ? 40 : "auto" }}>
              <AccountBalanceIcon sx={{
                color: "white",
                fontSize: "22px",
              }} />
            </ListItemIcon>
            {open &&
              <ListItemText
                primary="Services"
                primaryTypographyProps={{
                  style: {
                    cursor: 'pointer',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                  },
                }}
              />}

            {open && (tranascationOpen ?
              <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} /> :
              <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
            )}
          </ListItemButton>
        )}

        <Collapse in={tranascationOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            background: "rgba(16, 185, 129, 0.08)",
            borderRadius: "12px",
            mb: 1,
            overflow: "hidden",
          }}>
            <List component="div" disablePadding sx={{ py: 0.5 }}>

              <ListItemButton
                component={NavLink}
                to="/AppointmentIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(16, 185, 129, 0.3)",
                    borderLeft: "3px solid #10b981",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Appointment"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/DoctorEntryIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(16, 185, 129, 0.3)",
                    borderLeft: "3px solid #10b981",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Doctor Entry"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

              {isAppointmentNeeded && (
                <ListItemButton
                  component={NavLink}
                  to="/WeeklySchedule"
                  sx={{
                    pl: open ? 4 : 2,
                    py: 1.2,
                    mx: 1,
                    borderRadius: "8px",
                    color: "rgba(255,255,255,0.9)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      transform: "translateX(4px)",
                    },
                    "&.active": {
                      bgcolor: "rgba(16, 185, 129, 0.3)",
                      borderLeft: "3px solid #10b981",
                      color: "white",
                    },
                  }}
                >
                  {open &&
                    <ListItemText
                      primary="Weekly Template"
                      primaryTypographyProps={{
                        style: {
                          marginLeft: '20px',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          fontSize: '13px',
                          fontWeight: 500,
                          letterSpacing: "0.2px",
                        },
                      }} />}
                </ListItemButton>
              )}

              {isAppointmentNeeded && (
                <ListItemButton
                  component={NavLink}
                  to="/DoctorMontlyDiary"
                  sx={{
                    pl: open ? 4 : 2,
                    py: 1.2,
                    mx: 1,
                    borderRadius: "8px",
                    color: "rgba(255,255,255,0.9)",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      transform: "translateX(4px)",
                    },
                    "&.active": {
                      bgcolor: "rgba(16, 185, 129, 0.3)",
                      borderLeft: "3px solid #10b981",
                      color: "white",
                    },
                  }}
                >
                  {open &&
                    <ListItemText
                      primary="Doctor Diary"
                      primaryTypographyProps={{
                        style: {
                          marginLeft: '20px',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                          fontSize: '13px',
                          fontWeight: 500,
                          letterSpacing: "0.2px",
                        },
                      }} />}
                </ListItemButton>
              )}

            </List>
          </Box>
        </Collapse>

        {
          hasReadAccountsPermission && (
            <ListItemButton
              onClick={() => setAccountsOpen(!accountsOpen)}
              sx={{
                borderRadius: "12px",
                mb: 0.5,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(102, 126, 234, 0.15)",
                  transform: "translateX(4px)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : "auto" }}>
                <AccountBalanceWalletIcon sx={{
                  color: "white",
                  fontSize: "22px",
                }} />
              </ListItemIcon>
              {open &&
                <ListItemText
                  primary="Accounts"
                  primaryTypographyProps={{
                    style: {
                      cursor: 'pointer',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                    },
                  }}
                />}

              {open && (accountsOpen ?
                <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} /> :
                <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
              )}
            </ListItemButton>
          )
        }

        <Collapse in={accountsOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            background: "rgba(245, 158, 11, 0.08)",
            borderRadius: "12px",
            mb: 1,
            overflow: "hidden",
          }}>
            <List component="div" disablePadding sx={{ py: 0.5 }}>              

              <ListItemButton
                component={NavLink}
                to="/IncomeExpenseIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(245, 158, 11, 0.3)",
                    borderLeft: "3px solid #f59e0b",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Income & Expense"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>
              
            </List>
          </Box>
        </Collapse>

        {/* Pharmacy Section */}
        {hasReadMasterPermission && (
          <ListItemButton
            onClick={() => setPharmacyOpen(!pharmacyOpen)}
            sx={{
              borderRadius: "12px",
              mb: 0.5,
              transition: "all 0.3s ease",
              "&:hover": {
                background: "rgba(16, 185, 129, 0.15)",
                transform: "translateX(4px)",
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: open ? 40 : "auto" }}>
              <LocalPharmacy sx={{
                color: "white",
                fontSize: "22px",
              }} />
            </ListItemIcon>
            {open &&
              <ListItemText
                primary="Pharmacy"
                primaryTypographyProps={{
                  style: {
                    cursor: 'pointer',
                    fontFamily: 'system-ui, -apple-system, sans-serif',
                    fontSize: '14px',
                    fontWeight: 600,
                    letterSpacing: '0.3px',
                  },
                }}
              />}

            {open && (pharmacyOpen ?
              <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} /> :
              <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
            )}
          </ListItemButton>
        )}

        <Collapse in={pharmacyOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            background: "rgba(16, 185, 129, 0.08)",
            borderRadius: "12px",
            mb: 1,
            overflow: "hidden",
          }}>
            <List component="div" disablePadding sx={{ py: 0.5 }}>

              <ListItemButton
                component={NavLink}
                to="/PharmacyIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(16, 185, 129, 0.3)",
                    borderLeft: "3px solid #10b981",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Medicine Dispensing"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

            </List>
          </Box>
        </Collapse>

        {
          hasReadAdministrationPermission && (
            <ListItemButton
              onClick={() => setUserOpen(!userOpen)}
              sx={{
                borderRadius: "12px",
                mb: 0.5,
                transition: "all 0.3s ease",
                "&:hover": {
                  background: "rgba(102, 126, 234, 0.15)",
                  transform: "translateX(4px)",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: open ? 40 : "auto" }}>
                <People sx={{
                  color: "white",
                  fontSize: "22px",
                }} />
              </ListItemIcon>
              {open &&
                <ListItemText
                  primary="Auth Zen"
                  primaryTypographyProps={{
                    style: {
                      cursor: 'pointer',
                      fontFamily: 'system-ui, -apple-system, sans-serif',
                      fontSize: '14px',
                      fontWeight: 600,
                      letterSpacing: '0.3px',
                    },
                  }}
                />}
              {open && (userOpen ?
                <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} /> :
                <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
              )}
            </ListItemButton>

          )
        }

        <Collapse in={userOpen} timeout="auto" unmountOnExit>
          <Box sx={{
            background: "rgba(239, 68, 68, 0.08)",
            borderRadius: "12px",
            mb: 1,
            overflow: "hidden",
          }}>
            <List component="div" disablePadding sx={{ py: 0.5 }}>
              <ListItemButton
                component={NavLink}
                to="/UserIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(239, 68, 68, 0.3)",
                    borderLeft: "3px solid #ef4444",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Users"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/RoleIndex"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(239, 68, 68, 0.3)",
                    borderLeft: "3px solid #ef4444",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Roles"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>

              <ListItemButton
                component={NavLink}
                to="/PasswordChange"
                sx={{
                  pl: open ? 4 : 2,
                  py: 1.2,
                  mx: 1,
                  borderRadius: "8px",
                  color: "rgba(255,255,255,0.9)",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateX(4px)",
                  },
                  "&.active": {
                    bgcolor: "rgba(239, 68, 68, 0.3)",
                    borderLeft: "3px solid #ef4444",
                    color: "white",
                  },
                }}
              >
                {open &&
                  <ListItemText
                    primary="Reset Password"
                    primaryTypographyProps={{
                      style: {
                        marginLeft: '20px',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                        fontSize: '13px',
                        fontWeight: 500,
                        letterSpacing: "0.2px",
                      },
                    }} />}
              </ListItemButton>
            </List>
          </Box>
        </Collapse>

      </List >
    </Drawer >

  );
};

export default Sidebar;
